// External deps
import React, { useState, createContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import jwt from "jsonwebtoken";

// Internal deps
import { updateStorage, getItemStorage, clearStorage } from "../utils/utils";
import { showError } from "../utils/toastUtils";
import { constantsSession } from "../constants/constants";

import useLoading from "../hooks/useLoading";

import { getUserInfo } from "../adapters/loginAdapter";
import {
  getRoleUser,
  getRolePermissions,
  getCurrentProvider,
} from "../adapters/yclickMasterAdapter";

import { injectContext } from "../services/apiInterceptors";
import {
  URL_ADMINISTRACION,
  URL_CERTIFICACIONES,
  URL_SERVICIOS,
} from "../constants/host";

/**
 * @typedef AppsURL
 * @property {string} administracion
 * @property {string} certificaciones
 * @property {string} servicios
 */

export const AppContext = createContext();

const AppProvider = ({ children }) => {
  const accessToken = getItemStorage(constantsSession.ACCESS_TOKEN, false);
  const [userInfo, setUserInfo] = useState(
    getItemStorage(constantsSession.USER_INFO) || null
  );
  const [baseURL, setBaseURL] = useState("");
  const [auth, setAuth] = useState(!!accessToken);
  const [isReady, setIsReady] = useState(false);
  const [provName, setProvName] = useState(null);
  const [isNRURLReady, setIsNRURLReady] = useState(false);

  const { setLoading } = useLoading();
  const history = useHistory();
  const { search } = useLocation();
  const urlSearch = new URLSearchParams(search);

  const paramIsNR = urlSearch.get("nr_isnr") || "0";

  /** @type {AppsURL} */
  const appsURL = useMemo(
    () => ({
      administracion: URL_ADMINISTRACION,
      certificaciones: URL_CERTIFICACIONES,
      servicios: URL_SERVICIOS,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (paramIsNR === "1" && !isNRURLReady) {
    const queryNRisNotificationReport = paramIsNR;
    const queryNRContractNumber = decodeURIComponent(urlSearch.get("nr_cn"));
    const queryNRWell = decodeURIComponent(urlSearch.get("nr_wl"));
    const queryNRDateFrom = decodeURIComponent(urlSearch.get("nr_df"));
    const queryNRDateTo = decodeURIComponent(urlSearch.get("nr_dt"));

    updateStorage("hmnr_isnr", queryNRisNotificationReport);
    updateStorage("hmnr_cn", queryNRContractNumber);
    updateStorage("hmnr_wl", queryNRWell);
    updateStorage("hmnr_df", queryNRDateFrom);
    updateStorage("hmnr_dt", queryNRDateTo);

    setIsNRURLReady(true);

    history.replace({
      search: urlSearch.toString(),
    });
  }

  const getProvider = async () => {
    let providerName = "";

    try {
      const currentProviderResponse = await getCurrentProvider();
      providerName = encodeURI(currentProviderResponse);
    } catch (err) {
      showError(err);
      providerName = "errorProvider";
    } finally {
      setProvName(providerName);
    }
  };

  const notProvider = async () => {
    setProvName("notProvider");
  };

  const handleRoleUser = (resRoleUser) => {
    if (!resRoleUser?.length) {
      setBaseURL('/notFound');
      return;
    }
    const isAdmin = resRoleUser.some((rol) => rol.nombre.toLowerCase().includes("admin"));
    const isInspector = resRoleUser.some((rol) => rol.nombre.toLowerCase().includes("inspector"));
    const isProvider = resRoleUser.some((rol) => rol.nombre.toLowerCase().includes("proveedor"));
    // roles should not be duplicated
    // between administrator and inspector profiles
    if (isAdmin) {
      setBaseURL("/contratos");
    } else if (isInspector) {
      setBaseURL("/inspeccion");
    } else {
      setBaseURL("/notFound");
    }
    const resRoleUserNoId = resRoleUser.map((role) => ({ nombre: role.nombre }));
    updateStorage(constantsSession.ROLES_ME, resRoleUserNoId, true);
    //   Retrieve the specific provider to determine what should be displayed
    if (isProvider) {
      getProvider();
    } else {
      notProvider();
    }
  };
  const handleRolePermissions = (resRolePermissions) => {
    if (resRolePermissions) {
      updateStorage(constantsSession.ROLES_PERMISSIONS, resRolePermissions, true);
    }
  };
  const getRolesAndPermissions = () => {
    setLoading(true);
    Promise.all([getRoleUser(), getRolePermissions()])
      .then(([resRoleUser, resRolePermissions]) => {
        handleRoleUser(resRoleUser);
        handleRolePermissions(resRolePermissions);
        setAuth(true);
        setIsReady(true);
        setLoading(false)
      }).catch((err) => {
        setLoading(false);
        showError(err);
      });
  };

  const handleTokenJWT = (tokenJWT) => {
    if (tokenJWT?.token_details) {
      const { uid, first_name, last_name, mail } = tokenJWT.token_details.Profile;
      const newUserInfo = formatUserInfo(uid, first_name, last_name, mail, "-");
      updateStorage(constantsSession.USER_INFO, newUserInfo);
      setUserInfo(newUserInfo);
    } else {
      const defaultUserInfo = getDefaultUserInfo();
      updateStorage(constantsSession.USER_INFO, defaultUserInfo);
      setUserInfo(defaultUserInfo);
    }
    getRolesAndPermissions();
  };

  const handleUserInfoResponse = (res) => {
    const { uid, first_name, last_name, tipo_empleado, mail } = res;
    const newUserInfo = formatUserInfo(uid, first_name, last_name, mail, tipo_empleado);
    updateStorage(constantsSession.USER_INFO, newUserInfo);
    setUserInfo(newUserInfo);
    getRolesAndPermissions();
  };

  const handleUserInfoError = (err) => {
    const defaultUserInfo = getDefaultUserInfo();
    updateStorage(constantsSession.USER_INFO, defaultUserInfo);
    setUserInfo(defaultUserInfo);
    getRolesAndPermissions();
    setLoading(false);
    showError(err);
  };

  const formatUserInfo = (uid, first_name, last_name, mail, tipo_empleado) => {
    const firstName = first_name ? first_name.trim() : "Usuario";
    const lastName = last_name ? last_name.trim() : "Usuario";
    return {
      uid,
      first_name,
      last_name,
      tipo_empleado,
      mail,
      id: uid,
      name: `${firstName} ${lastName}`,
      type: tipo_empleado,
    };
  };

  const getDefaultUserInfo = () => ({
    uid: "-",
    first_name: "-",
    last_name: "-",
    tipo_empleado: "-",
    mail: "-",
    id: "-",
    name: "Usuario Usuario",
    type: "-",
  });

  const getInfoUser = async (token) => {
    setLoading(true);

    const tokenJWT = jwt.decode(token);
    if (tokenJWT) {
      handleTokenJWT(tokenJWT);
    } else {
      try {
        const res = await getUserInfo(token);
        handleUserInfoResponse(res);
      } catch (err) {
        handleUserInfoError(err);
      }
    }
  };


  const authUser = (data, date) => {
    setLoading(true);
    const { access_token, refresh_token, expires_in } = data;
    updateStorage(constantsSession.ACCESS_TOKEN, access_token, false);
    updateStorage(constantsSession.ACCESS_TOKEN_DATE, date, false);
    updateStorage(constantsSession.ACCESS_TOKEN_EXPIRE_IN, expires_in, false);
    updateStorage(constantsSession.REFRESH_TOKEN, refresh_token, false);
    // Información del usuario
    getInfoUser(access_token);
  };

  const onLogOut = () => {
    clearStorage();
    setUserInfo(null);
    setAuth(false);
    history.push("/login");
  };

  useEffect(() => {
    const token = getItemStorage(constantsSession.ACCESS_TOKEN, false);
    if (token) {
      setAuth(true);
    }
  }, []);

  useEffect(() => {
    injectContext({
      onLogOut,
    });

    urlSearch.delete("nr_isnr");
    urlSearch.delete("nr_cn");
    urlSearch.delete("nr_wl");
    urlSearch.delete("nr_df");
    urlSearch.delete("nr_dt");
    urlSearch.delete("isOyM");
    history.replace({
      search: urlSearch.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppContext.Provider
      value={{
        appsURL,
        userInfo,
        authUser,
        auth,
        isReady,
        provName,
        baseURL,
        onLogOut,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;

AppProvider.propTypes = {
  children: PropTypes.element.isRequired,
};

AppProvider.defaultProps = {};
